import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { prepareDownloadWindow, saveFile$ } from '@kp/common/functions';
import { AGREEMENTS, CALCULATIONS, PAYMENT_GATEWAYS } from '@kp/env';
import { LogService } from '@kp/logs';
import {
  calculationToModel,
  modelToAgreement,
  modelToCalculation,
  updateModelWithPaths,
} from '@kp/tiia-functions';
import {
  dateToISO,
  deepmerge,
  normalizeDate,
  set,
  setMut,
  unwrap,
} from '@kp/utils';
import { RxState } from '@rx-angular/state';
import {
  BehaviorSubject,
  catchError,
  debounce,
  debounceTime,
  filter,
  forkJoin,
  map,
  of,
  race,
  Subject,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';

import type {
  CancelVariant,
  Card,
  GoPayInterface,
  KbPaymentInterface,
  QrPaymentStart,
  SyncCalculation,
  TiiaCalculation,
  TiiaDocument,
  TiiAgreement,
  TiiaModel,
  TiiaPayment,
  TiiaStore,
  TIResponse,
} from '@kp/interfaces';
import type { DeepPartial, DottedObjPaths } from '@kp/types';
import type { Call, O } from 'hotscript';
import type { Observable } from 'rxjs';

type TiiaModelUpdate = readonly [
  path: DottedObjPaths<TiiaModel>,
  value: unknown,
];

@Service()
class TiiaService {
  readonly #http = inject(HttpClient);
  readonly #logß = inject(LogService);
  readonly #router = inject(Router);

  #step = 0;
  readonly #store = new RxState<TiiaStore>();
  readonly #sync$ = new Subject<SyncCalculation>();
  /**
   * undefined - payment was not started
   * true - payment succeeded
   * false - payment failed
   */
  readonly payment$ = new BehaviorSubject<boolean | undefined>(undefined);
  readonly setLoading$ = new Subject<boolean | string>();
  readonly showLoading$ = this.setLoading$
    .asObservable()
    .pipe(debounce(() => (this.#step > 1 ? timer(100) : timer(400))));

  // oxlint-disable-next-line max-lines-per-function
  constructor() {
    this.#store.set({
      documents: [],
      model: {
        additionalAgreementInfo: {
          adults: 1,
          children: 0,
          destination: 'E',
          destinationCountryCodes: '',
          family: 'false',
          isAbroad: false,
          pensioners: 0,
          referralCode: 'KP_WEB',
          students: 0,
        },
        endDate: dateToISO(normalizeDate()),
        temp: {
          beginDate: dateToISO(normalizeDate()),
          parties: {},
          party: this.defaultParty,
          sportFirstRender: [],
          workFirstRender: [],
        },
      },
    });

    this.#sync$
      .pipe(
        debounceTime(0),
        switchMap(({ model, result$, url }) =>
          this.#http.post<TIResponse>(url, model).pipe(
            filter(Boolean),
            tap(resp => {
              const response = Object.freeze(resp);

              if (this.#step === 1 && !this.model.temp.recommended) {
                this.updateModelByObject(calculationToModel(response));
              }

              this.#logß.log(['resp:', response]);

              this.#store.set({ calculation: response });
              result$.next(response);

              // this.showButtonLoader(false);
            }),
            catchError(() => of(undefined)),
          ),
        ),
      )
      // eslint-disable-next-line @smarttools/rxjs/no-ignored-subscribe
      .subscribe();
  }

  #openAndroidDocument(url: string): void {
    const targetWindow = globalThis.open('about:blank', '_blank');

    if (!targetWindow) {
      return;
    }

    targetWindow.opener = null;

    this.#http.get<{ url: string }>(url).subscribe({
      next: ({ url: cardUrl }) => {
        targetWindow.location.replace(cardUrl);
      },
      error: () => {
        targetWindow.close();
      },
    });
  }

  get calculation(): TiiaCalculation {
    return this.#store.get('calculation');
  }

  get calculation$(): Observable<TIResponse> {
    return this.#store.select('calculation');
  }

  get cards(): Record<string, Array<Card>> | undefined {
    const docs = this.#store.get('documents');

    if (isDocument(docs)) {
      return undefined;
    }

    if (Array.isArray(docs)) {
      return docs.at(0)?.cards;
    }

    return docs.cards;
  }

  confirmAgreement(): Observable<unknown> {
    return this.#store.select('agreement').pipe(
      take(1),
      switchMap(agreement =>
        this.#http
          .patch<TiiAgreement>(`/${AGREEMENTS}`, {
            additionalAgreementInfo: {},
            agreementNumber: agreement.agreementNumber,
            agreementStatusCode: 'PR',
            productCode: '50202',
          })
          .pipe(
            switchMap(() =>
              forkJoin({
                documents: this.fetchDocuments$(agreement.agreementNumber),
                paymentMethods: this.fetchPaymentMethods$(),
                qr: this.startQrPayment$(agreement.agreementNumber),
              }),
            ),
          ),
      ),
    );
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  get defaultParty(): TiiaModel['temp']['party'] {
    return {
      birthDate: '',
      conscriptionNumber: '',
      email: '',
      municipality: '',
      name: '',
      onlyPolicy: false,
      phone: '',
      sport: [],
      street: '',
      streetInput: '',
      strictOnlyPolicy: false,
      surname: '',
      work: [],
      zipCode: '',
    };
  }

  get documents(): Array<TiiaDocument> {
    const docs = this.#store.get('documents');

    if (isDocument(docs)) {
      return docs;
    }

    if (Array.isArray(docs)) {
      return docs.at(0)?.documents || [];
    }

    return docs?.documents || [];
  }

  fetchDocuments$(agreementNumber: string): Observable<Array<TiiaDocument>> {
    return this.#http
      .get<Array<TiiaDocument>>(
        `/${AGREEMENTS}/${agreementNumber}/documents?formatter=TRAVEL`,
      )
      .pipe(
        catchError(() => of([])),
        tap(documents => {
          this.#store.set('documents', () => documents);
        }),
      );
  }

  fetchPaymentMethods$(): Observable<Array<TiiaPayment>> {
    return this.#http
      .get<Array<TiiaPayment>>(
        `/${PAYMENT_GATEWAYS}/payments?productCode=50202`,
      )
      .pipe(
        tap(payments => {
          this.#store.set('payments', () => payments);
        }),
      );
  }

  getCustomCalculation$(
    amount: number,
    variant: CancelVariant,
    date: string,
  ): Observable<TIResponse> {
    return this.model$.pipe(
      map(model => {
        const m = structuredClone(model);

        setMut(m, 'temp.stornoAmount', amount);
        setMut(m, 'temp.stornoVariant', variant);
        setMut(m, 'additionalAgreementInfo.travelPaymentDate', date);

        return m;
      }),
      map(modelToCalculation),
      switchMap(model =>
        this.#http.post<TIResponse>(
          `/${CALCULATIONS}/premiumCalculation?calculateOtherPremiums=true`,
          model,
        ),
      ),
    );
  }

  get model(): TiiaModel {
    return this.#store.get('model');
  }

  get model$(): Observable<TiiaModel> {
    return this.#store.select('model');
  }

  navigateToStep(step: number): void {
    this.#router.navigate([this.#router.config[step]?.path || '']);
  }

  pay(method: 'kb'): Observable<KbPaymentInterface>;
  pay(method: 'gopay'): Observable<GoPayInterface>;
  pay(method: 'gopay' | 'kb'): Observable<GoPayInterface | KbPaymentInterface> {
    return race(
      this.#store.select('agreement'),
      this.#store
        .select('agreementNumber')
        .pipe(map(agreementNumber => ({ agreementNumber }))),
    ).pipe(
      take(1),
      switchMap(({ agreementNumber }) =>
        this.#http.post<{ specData: { redirectUrl: string } }>(
          `/${PAYMENT_GATEWAYS}/payments/pm-${method}/start`,
          { agreementNumber },
        ),
      ),
    );
  }

  get payments(): Array<TiiaPayment> | undefined {
    return this.#store.get('payments');
  }

  saveFile(file: string): void {
    const agreementNumber =
      this.#store.get('agreement')?.agreementNumber ||
      unwrap(
        this.#store.get('agreementNumber'),
        'No agreement number was passed',
      );

    const url = `/${AGREEMENTS}/${agreementNumber}/documents/${file}`;

    if (file.includes('ANDROID')) {
      this.#openAndroidDocument(url);

      return;
    }

    const targetWindow = prepareDownloadWindow();

    saveFile$({
      http: this.#http,
      targetWindow,
      url,
      // eslint-disable-next-line @smarttools/rxjs/no-ignored-subscribe
    }).subscribe();
  }

  sendAgreement(): Observable<Array<TiiaDocument>> {
    return this.#store
      .select(['calculation', 'model'], ({ calculation, model }) =>
        modelToAgreement(calculation, model),
      )
      .pipe(
        take(1),
        switchMap(agr => {
          const { agreementNumber: agreementNum, id } = this.#store.get(
            'agreement',
          ) || { agreementNumber: '' };

          return (
            this.#http[agreementNum ? 'put' : 'post'](
              `/${AGREEMENTS}`,
              agreementNum
                ? { ...agr, agreementNumber: agreementNum, id }
                : agr,
            ) as Observable<TiiAgreement>
          ).pipe(
            tap(agreement => {
              if (!agreement) {
                return;
              }

              this.#store.set('agreement', () => agreement);
            }),
            switchMap(agreement =>
              this.#http.get<Array<TiiaDocument>>(
                `/${AGREEMENTS}/${
                  agreement ? agreement.agreementNumber : agreementNum
                }/documents`,
              ),
            ),
            tap(documents => {
              this.#store.set('documents', () => documents);
            }),
          );
        }),
      );
  }

  setAgreementNumber(agreementNumber: string): void {
    this.#store.set('agreementNumber', () => agreementNumber);
  }

  setStep(step: number): void {
    this.#step = step;
  }

  startQrPayment$(agreementNumber: string): Observable<QrPaymentStart> {
    return this.#http
      .post<QrPaymentStart>(`/${PAYMENT_GATEWAYS}/payments/pm-qr/start`, {
        agreementNumber,
      })
      .pipe(
        tap(qrData => {
          this.#store.set('payments', ({ payments }) =>
            payments.map(payment => {
              if (payment.name !== 'pm-qr') {
                return payment;
              }

              return { ...payment, qrData };
            }),
          );
        }),
      );
  }

  syncModel(result?: false): void;
  syncModel(result: true): Observable<TIResponse>;
  syncModel(result = false): Observable<TIResponse> | undefined {
    const model = modelToCalculation(
      this.model,
    ) as DeepPartial<TiiaCalculation>;

    const result$ = new Subject<TIResponse>();

    this.#sync$.next({
      model,
      result$,
      url: `/${CALCULATIONS}/premiumCalculation?calculateOtherPremiums=true`,
    });

    return result ? result$.asObservable().pipe(take(1)) : undefined;
  }

  updateModel(model: ReadonlyArray<TiiaModelUpdate>): void;
  updateModel<Path extends DottedObjPaths<TiiaModel>>(
    path: Path,
    value: Partial<Call<O.Get<Path>, TiiaModel>>,
  ): void;
  updateModel<Path extends DottedObjPaths<TiiaModel>>(
    modelOrPath: Path | ReadonlyArray<TiiaModelUpdate>,
    value?: Partial<Call<O.Get<Path>, TiiaModel>>,
  ): void {
    if (Array.isArray(modelOrPath)) {
      const obj = {};
      for (const [p, v] of modelOrPath) {
        setMut(obj, p, v);
      }
      this.updateModelByObject(obj);

      return;
    }

    this.updateModelByObject(set({}, modelOrPath as Path, value));
  }

  updateModelByObject(model: DeepPartial<TiiaModel>, forceUpdate?: true): void {
    const oldModel = this.model;
    const updatedModel = deepmerge(oldModel, model, {
      arrayMerge: (_destinationArray, sourceArray, _options) => sourceArray,
    }) as TiiaModel;

    const [paths] = updateModelWithPaths(oldModel, model);

    if (paths.length === 0 && !forceUpdate) {
      return;
    }

    this.#logß.log([paths, updatedModel]);

    this.#store.set({
      model: Object.freeze(updatedModel),
    });
  }

  updateModelByReplaceObject(path: string, object: object): void {
    const updatedModel = set(this.model, path, object);

    this.#store.set({ model: Object.freeze(updatedModel) });
  }
}

function isDocument(
  document:
    | Array<TiiaDocument>
    | Array<{
        cards: Record<string, Array<Card>>;
        documents: Array<TiiaDocument>;
      }>
    | { cards: Record<string, Array<Card>>; documents: Array<TiiaDocument> }
    | undefined,
): document is Array<TiiaDocument> {
  return (
    Array.isArray(document) &&
    (document.length === 0 || 'fileName' in (document.at(0) as TiiaDocument))
  );
}

export { TiiaService };
